import { useMemo, useRef } from 'react';
import { useCardDashboardStationeryTemplatesContainerQuery, useCardDashboardStationeryTemplatesSummaryQuery } from './DesignsGallery.graphql.generated';
import { useIsPublicGallerySsr } from './useIsPublicGallerySsr';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { withWindow } from '@shared/utils/withWindow';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { DraftJson } from '@apps/card/routes/CardCustomizer';
import type { StationeryTemplate } from './DesignsGallery.types';
import { processInitialQueryData, tileGroupProductFacetTags, themeIdWithOrientation, parseDisplayOrderEntry } from './utils';
import { injectProducts } from './injectProducts';
import type { FacetOptionLocal } from './components/Filters/Filter.types';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';
import { FREE_DIGITAL_DUMMY_PRODUCT } from '@apps/card/components/ProductUIConfig/freeDigitalProduct';
import { useProducts } from './useProducts';
import { useWebsiteThemeId } from './WebsiteThemeIdContext';
import { useCanUseFreeCards } from '@shared/utils/eventInfo/useCanUseFreeCards';
import { isEventReadPending } from '@shared/utils/eventInfo/isEventReadPending';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';

type UseDesignTemplatesArgs = {
  eventId?: string;
  categories?: StationeryTemplateCategoryEnum[];
  themeIds?: string[];
  skipDigitalDesigns?: boolean;
};

export type StationeryTemplateWithDraftJson = StationeryTemplate & { themeJSON: DraftJson };

/*
 * Resolves the `printGalleryThemeDisplayOrder` flag into the ordered theme list
 * for the current category. The payload is either a flat array (applies to every
 * category) or a `{ [category]: string[] }` map. Returns undefined when the flag
 * is off or has no list for this category.
 *
 * Expected object format:
 * {
 *   "saveTheDate": [ ... ],
 *   "enclosure": [
 *     "wide modern_monogram+navy",
 *     "tall upload_your_design",
 *     "weekend_events+blush",
 *     "tall modern_monogram+navy"
 *   ],
 *   ...
 * }
 */
export const resolveThemeDisplayOrder = (value: string | undefined, payload: unknown, currentCategory: StationeryTemplateCategoryEnum): string[] | undefined => {
  const hasDisplayOrder = !!value && value !== 'off';
  if (!hasDisplayOrder) return undefined;
  if (Array.isArray(payload)) return payload as string[];
  if (payload && typeof payload === 'object') {
    const themeIdList = (payload as Record<string, string[]>)[currentCategory];
    if (Array.isArray(themeIdList)) return themeIdList;
  }
  return undefined;
};

/*
 * The ordered theme list from the gallery-order flag for the current category.
 */
export const useThemeDisplayOrder = (): string[] | undefined => {
  const { currentCategory } = useCardCategoryContext();
  const { value, payload } = useFeatureValue('printGalleryThemeDisplayOrder');
  return useMemo(() => resolveThemeDisplayOrder(value, payload, currentCategory), [value, payload, currentCategory]);
};

/*
 * The themeId to promote to the front of the gallery: the `theme` URL param (read
 * once on mount) or the website's theme. Shared so the base sort and the
 * search-inactive expansion promote the same theme.
 */
export const usePriorityThemeId = (): string | undefined => {
  const websiteThemeId = useWebsiteThemeId();
  const [themeQueryParam] = useManagedSearchParam('theme');
  const themeQueryParamRef = useRef(themeQueryParam);
  return themeQueryParamRef.current || websiteThemeId;
};

/*
 * This method allows setting order of top N items in the gallery based on array of themeIds coming from Amplitude Feature flag
 */
const setGalleryOrder = (templates: StationeryTemplate[], themeOrder: string[]): StationeryTemplate[] => {
  if (templates?.length < 1 || themeOrder?.length < 1) return templates;
  try {
    // Use first occurrence so duplicates don't push a theme to a later position.
    // Colorway (`+colorway`) is stripped here — it only matters for the
    // search-inactive repeat expansion, not for ordering the base (deduped) list.
    const themeLookup = new Map<string, number>();
    themeOrder.forEach((entry, index) => {
      const { orientationKey, themeId } = parseDisplayOrderEntry(entry);
      if (!themeLookup.has(orientationKey)) themeLookup.set(orientationKey, index);
      // Only bare entries position a template regardless of orientation; an
      // orientation-prefixed entry must not order a differently-oriented variant.
      if (orientationKey === themeId && !themeLookup.has(themeId)) themeLookup.set(themeId, index);
    });
    return [...templates].sort((a, b) => {
      const aIndex = themeLookup.get(themeIdWithOrientation(a)) ?? themeLookup.get(a?.themeId) ?? Number.POSITIVE_INFINITY;
      const bIndex = themeLookup.get(themeIdWithOrientation(b)) ?? themeLookup.get(b?.themeId) ?? Number.POSITIVE_INFINITY;
      return aIndex - bIndex;
    });
  } catch {
    console.error('Error sorting templates by printGalleryThemeDisplayOrder.  Using default sort order');
    return templates;
  }
};

const useDesignTemplatesCore = (currentCategory: StationeryTemplateCategoryEnum, args: UseDesignTemplatesArgs) => {
  const { eventId, categories, themeIds, skipDigitalDesigns } = args;
  const shouldSkipFetchingDigitalDesignData = !(currentCategory === StationeryTemplateCategoryEnum.invitation || currentCategory === StationeryTemplateCategoryEnum.saveTheDate);

  const summaryVariables = {
    filter: {
      categories: categories ?? [currentCategory],
      side: 'front' as const,
      themeIds,
    },
    // Requesting a number larger than what we have in the DB while we wait on
    // designs for pagination
    first: 20000,
    includeOptions: {
      includeFavoritesByEventIds: eventId ? [eventId] : undefined,
      includeProducts: true,
    },
  };

  // The server-rendered summary (PRNT-5494): the gallery minus `themeJSON`, fetched on the server
  // and read from the embedded cache on the client, so a server-rendered load keeps its tiles until
  // the full query lands. `cache-only` on the client: a category change must not fetch the same
  // templates twice.
  const isPublicGallerySsr = useIsPublicGallerySsr();
  const { data: summaryData, error: summaryError } = useCardDashboardStationeryTemplatesSummaryQuery({
    variables: summaryVariables,
    batchMode: 'fast',
    fetchPolicy: withWindow(() => 'cache-only' as const, 'cache-first' as const),
    ssr: true,
    skip: !isPublicGallerySsr,
  });

  const { data: templateData, error } = useCardDashboardStationeryTemplatesContainerQuery({
    variables: { ...summaryVariables, skipDigitalDesigns: skipDigitalDesigns || shouldSkipFetchingDigitalDesignData },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: false,
  });

  if (error) throw error;
  if (summaryError) throw summaryError;

  // A `cache-only` miss answers with an empty object, not `undefined`; only a summary that carries
  // the templates counts. Full data wins once it lands: its nodes merge over the summary's by id.
  const summary = summaryData?.stationeryTemplatesContainer ? summaryData : undefined;
  const galleryData = templateData ?? summary;
  const isBuiltFromSummary = !templateData && !!summary;

  const templatesWithProductIds = useMemo(() => {
    return processInitialQueryData(galleryData, currentCategory, { isSummary: isBuiltFromSummary });
  }, [galleryData, currentCategory, isBuiltFromSummary]);

  return templatesWithProductIds;
};

/*
 * A design whose only product is the free ecard: a digital design with no purchasable product of
 * its own. Matched the same way the dummy injection below matches it — an empty product list after
 * `injectProducts` — rather than by theme name.
 */
export const isFreeDigitalOnlyTemplate = (template: Pick<StationeryTemplate, 'format' | 'products'>): boolean => template.format === 'digital' && template.products.length === 0;

export const useDesignTemplates = (args: UseDesignTemplatesArgs) => {
  const { currentCategory } = useCardCategoryContext();
  const templatesWithProductIds = useDesignTemplatesCore(currentCategory, args);
  const products = useProducts();
  const canUseFreeCards = useCanUseFreeCards();
  const { isPublicShopRoute } = useCardsRouterContext();
  // Empty until the event settles, so the gallery holds its skeleton rather than dropping tiles
  // mid-render. The public shop has no event to settle: its provider only leaves "initial load" in
  // a client effect, so waiting there would hold the server render on the skeleton for good.
  const eventInfo = useEventInfo();
  const isFreeCardsDecisionPending = !isPublicShopRoute && isEventReadPending(eventInfo);

  const themeOrder = useThemeDisplayOrder();
  const priorityThemeId = usePriorityThemeId();

  const templates = useMemo(() => {
    if (isFreeCardsDecisionPending) return [];
    const injected = injectProducts(templatesWithProductIds, products);

    // MGT-109: a new-stack wedding cannot send the free ecard (Evan, 2026-09-30), so a design that
    // sells nothing else is not offered at all. The single filter point for every list built on
    // this hook — category gallery, search, facets and the PDP's matching-designs rail.
    const templatesWithProducts = canUseFreeCards ? injected : injected.filter(template => !isFreeDigitalOnlyTemplate(template));

    // Digital-only designs have no purchasable product (empty productIds), so give
    // them the synthetic free basic-digital product. This drives the tile's
    // "Free Digital" price and the `format:digital` tag below through the normal
    // product pipeline, mirroring how the PDP appends it for paper+digital themes.
    for (const template of templatesWithProducts) {
      if (template.format === 'digital' && template.products.length === 0) {
        template.products = [FREE_DIGITAL_DUMMY_PRODUCT];
      }
    }

    // Conceptually part of `processedInitialPrintableTemplates`. However this part can only be done after
    // injectProducts. TODO: Consider all `mappedTags`logic to post-injectProducts.
    // `injectProducts` shallow-clones each template, so `mappedTags` is still the array cached by
    // `useDesignTemplatesCore`: build new template objects with a new array rather than push, or
    // every re-run of this memo (products / themeOrder change) would append the tags again.
    const templatesWithFacetTags: StationeryTemplate[] = templatesWithProducts.map(template => {
      const templateFormats = new Set<FacetOptionLocal>(template.products.map(p => (isDigitalProduct(p) ? 'format:digital' : 'format:paper')));
      // Product facets come from the whole tile group (the magnet product links to the postcard
      // siblings, not the 5x7 that faces the tile); `products` stays the tile's own for pricing.
      return { ...template, mappedTags: [...template.mappedTags, ...Array.from(templateFormats), ...tileGroupProductFacetTags(template, products)] };
    });

    const sortedTemplates: StationeryTemplate[] = themeOrder ? setGalleryOrder(templatesWithFacetTags, themeOrder) : [...templatesWithFacetTags];

    // Promoting certain templates to first place
    if (priorityThemeId) {
      const priorityIndex = sortedTemplates.findIndex(template => template.themeId === priorityThemeId);
      if (priorityIndex > 0) {
        const [priorityTemplate] = sortedTemplates.splice(priorityIndex, 1);
        sortedTemplates.unshift(priorityTemplate);
      }
    }

    return sortedTemplates;
  }, [templatesWithProductIds, products, themeOrder, priorityThemeId, canUseFreeCards, isFreeCardsDecisionPending]);

  return templates;
};
